// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from './Button';
import { Input } from './Input';
import { api } from '../lib/api';
import { useAuthStore } from '../store/authStore';
import { useGameStore } from '../store/gameStore';

export function MagicLink() {
  const { roomCode } = useParams<{ roomCode: string }>();
  const navigate = useNavigate();
  const { sessionId, playerId, displayName, setSession, updateGameState, clearSession } =
    useGameStore();
  const { isAuthenticated, user } = useAuthStore();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [targetSessionId, setTargetSessionId] = useState<string | null>(null);

  // Helper to format room code for display
  const formatRoomCodeForDisplay = (code: string): string => {
    const parts = code.split('-');
    if (parts.length !== 6) return code;
    const formatted = [];
    for (let i = 0; i < 6; i += 2) {
      formatted.push(`${parts[i]} ${parts[i + 1]}`);
    }
    return formatted.join(', ');
  };

  useEffect(() => {
    if (!roomCode) {
      setError('Invalid magic link');
      setLoading(false);
      return;
    }

    // Lookup the room first
    const lookupRoom = async () => {
      try {
        const response = await api.lookupRoomByCode(roomCode);
        setTargetSessionId(response.session_id);

        // Check if user is authenticated
        if (isAuthenticated() && user) {
          // Authenticated user - auto-join with their username
          await autoJoinAsAuthenticatedUser(response.session_id, user.username);
        } else if (sessionId && playerId && displayName) {
          // User has existing game session credentials - show resume dialog
          setShowResumeDialog(true);
        } else {
          // No credentials - show join form for guest
          setShowJoinForm(true);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Room not found');
      } finally {
        setLoading(false);
      }
    };

    lookupRoom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomCode, sessionId, playerId, displayName, isAuthenticated, user]);

  // Auto-join for authenticated users
  const autoJoinAsAuthenticatedUser = async (targetSessionId: string, username: string) => {
    setSubmitting(true);
    try {
      const response = await api.joinRoom({
        session_id: targetSessionId,
        display_name: username,
      });

      setSession(
        response.session_id,
        response.room_code,
        response.player_id,
        response.player_token,
        username
      );

      // Set initial lobby state
      const isHost = response.current_players[0]?.player_id === response.player_id;
      updateGameState({
        room_status: 'lobby',
        game_id: '',
        current_players: response.current_players,
        your_player_id: response.player_id,
        is_host: isHost,
        player_action_statuses: {},
      } as unknown as Parameters<typeof updateGameState>[0]);

      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join room');
      setShowJoinForm(true); // Fallback to join form on error
    } finally {
      setSubmitting(false);
    }
  };

  const handleResume = () => {
    // User wants to resume with existing credentials
    // Check if they're already in the target session
    if (sessionId === targetSessionId) {
      // Already in this session, just navigate to lobby
      navigate('/');
    } else {
      // Different session - clear old and join new with same name
      handleJoinAsExisting();
    }
  };

  const handleJoinAsExisting = async () => {
    if (!targetSessionId || !displayName) return;

    setSubmitting(true);
    try {
      const response = await api.joinRoom({
        session_id: targetSessionId,
        display_name: displayName,
      });

      setSession(
        response.session_id,
        response.room_code,
        response.player_id,
        response.player_token,
        displayName
      );

      // Set initial lobby state
      const isHost = response.current_players[0]?.player_id === response.player_id;
      updateGameState({
        room_status: 'lobby',
        game_id: '',
        current_players: response.current_players,
        your_player_id: response.player_id,
        is_host: isHost,
        player_action_statuses: {},
      } as unknown as Parameters<typeof updateGameState>[0]);

      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join room');
    } finally {
      setSubmitting(false);
    }
  };

  const handleJoinAsNew = () => {
    // Clear existing credentials and show join form
    clearSession();
    setShowResumeDialog(false);
    setShowJoinForm(true);
  };

  const handleJoinWithNewName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetSessionId || !newDisplayName.trim()) return;

    setSubmitting(true);
    try {
      const response = await api.joinRoom({
        session_id: targetSessionId,
        display_name: newDisplayName.trim(),
      });

      setSession(
        response.session_id,
        response.room_code,
        response.player_id,
        response.player_token,
        newDisplayName.trim()
      );

      // Set initial lobby state
      const isHost = response.current_players[0]?.player_id === response.player_id;
      updateGameState({
        room_status: 'lobby',
        game_id: '',
        current_players: response.current_players,
        your_player_id: response.player_id,
        is_host: isHost,
        player_action_statuses: {},
      } as unknown as Parameters<typeof updateGameState>[0]);

      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join room');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="glass rounded-lg p-8 text-center">
          <div className="animate-spin h-8 w-8 border-4 border-white border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>Looking up room...</p>
          {roomCode && (
            <p className="text-sm text-gray-300 mt-2">{formatRoomCodeForDisplay(roomCode)}</p>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="glass rounded-lg p-8 text-center max-w-md">
          <h2 className="text-2xl font-bold mb-4 text-red-400">Error</h2>
          <p className="mb-6">{error}</p>
          <Button variant="primary" onClick={handleCancel}>
            Go to Room List
          </Button>
        </div>
      </div>
    );
  }

  // Resume dialog
  if (showResumeDialog && displayName) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="glass rounded-lg p-8 text-center max-w-md">
          <h2 className="text-2xl font-bold mb-4">Resume Session?</h2>
          <p className="mb-6">
            Would you like to resume as <strong className="text-purple-300">{displayName}</strong>?
          </p>
          {roomCode && (
            <p className="text-sm text-gray-300 mb-6">
              Joining: {formatRoomCodeForDisplay(roomCode)}
            </p>
          )}
          <div className="space-y-3">
            <Button
              variant="success"
              className="w-full"
              onClick={handleResume}
              disabled={submitting}
            >
              {submitting ? 'Joining...' : `Resume as ${displayName}`}
            </Button>
            <Button
              variant="secondary"
              className="w-full"
              onClick={handleJoinAsNew}
              disabled={submitting}
            >
              Join as Someone Else
            </Button>
            <Button
              variant="secondary"
              className="w-full"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Join form for new user (guest)
  if (showJoinForm) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="glass rounded-lg p-8 max-w-md w-full">
          <h2 className="text-2xl font-bold mb-4">Join Room as Guest</h2>
          {roomCode && (
            <p className="text-sm text-gray-300 mb-6">
              Magic Words: <strong>{formatRoomCodeForDisplay(roomCode)}</strong>
            </p>
          )}
          <form onSubmit={handleJoinWithNewName} className="space-y-4">
            <Input
              label="Your Name"
              value={newDisplayName}
              onChange={e => setNewDisplayName(e.target.value)}
              placeholder="Enter your display name"
              required
              maxLength={50}
            />
            <div className="flex gap-3">
              <Button type="submit" variant="success" disabled={submitting} className="flex-1">
                {submitting ? 'Joining...' : 'Join as Guest'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handleCancel}
                disabled={submitting}
              >
                Cancel
              </Button>
            </div>
          </form>
          <div className="mt-4 pt-4 border-t border-white/20">
            <p className="text-sm text-gray-400 text-center mb-2">Already have an account?</p>
            <Button variant="primary" onClick={() => navigate('/login')} className="w-full">
              Log In Instead
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
